import React from 'react';
import {PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend} from 'recharts';
import './ServicesSummarySection.css'

interface ServicesSummaryData {
    serviceType: string;
    serviceName: string;
    total: number;
    byCategory: Record<string, number>;
}

interface PieChartData {
    name: string;
    value: number;
    category: string;
}

interface ServicesSummaryProps {
    title: string;
    data: ServicesSummaryData[];
    columns: Array<{ key: string; label: string }>;
    pieChartData: PieChartData[];
    pieChartTitle: string;
    colors: string[];
    totalEntries: number;
    selectedUserGroup?: string | null;
    getSelectedGroupName: () => string;
    showColumnsWithZeroEntries: boolean;
}

const ServicesSummarySection: React.FC<ServicesSummaryProps> = ({
                                                                    title,
                                                                    data,
                                                                    columns,
                                                                    pieChartData,
                                                                    pieChartTitle,
                                                                    colors,
                                                                    totalEntries,
                                                                    selectedUserGroup,
                                                                    getSelectedGroupName,
                                                                    showColumnsWithZeroEntries
                                                                }) => {
    const totalServices = data.reduce((sum, s) => sum + s.total, 0);
    const activeServiceTypes = data.filter(s => s.total > 0).length;

    // Calculate column totals and filter if needed
    const columnsWithTotals = columns.map(column => ({
        ...column,
        total: data.reduce((sum, service) => sum + (service.byCategory[column.key] || 0), 0)
    }));

    const visibleColumns = showColumnsWithZeroEntries
        ? columnsWithTotals
        : columnsWithTotals.filter(column => column.total > 0);

    return (
        <div className="analysis-card std-box dropshadow" style={{display: 'inline-block',  width: 'auto', marginRight: '20px'}}>
            <h2 className="summary-title">
                {title}{` (${getSelectedGroupName()})`}
            </h2>
            <div className="services-summary-container">
                <div className="services-summary-layout">
                    <div className="services-summary-table">
                        <table className="entries-table">
                            <thead>
                            <tr>
                                <th>Tjänst</th>
                                {visibleColumns.map(column => (
                                    <th key={column.key}>{column.label}</th>
                                ))}
                                <th>Totalt</th>
                            </tr>
                            </thead>
                            <tbody>
                            {data.map((service) => (
                                <tr key={service.serviceType} style={{
                                    opacity: service.total === 0 ? 0.6 : 1,
                                    backgroundColor: service.total === 0 ? '#f9f9f9' : 'transparent'
                                }}>
                                    <td style={{
                                        color: service.total === 0 ? '#999' : 'inherit'
                                    }}>
                                        {service.serviceName}
                                    </td>
                                    {visibleColumns.map(column => (
                                        <td key={column.key} style={{
                                            textAlign: 'center',
                                            color: service.total === 0 ? '#999' : 'inherit'
                                        }}>
                                            {service.byCategory[column.key] || 0}
                                        </td>
                                    ))}
                                    <td style={{
                                        textAlign: 'center',
                                        fontWeight: service.total > 0 ? 'bold' : 'normal',
                                        color: service.total === 0 ? '#999' : 'inherit'
                                    }}>
                                        {service.total}
                                    </td>
                                </tr>
                            ))}
                            <tr style={{
                                borderTop: '2px solid #ddd',
                                backgroundColor: '#f0f0f0',
                                fontWeight: 'bold'
                            }}>
                                <td>Totalt alla tjänster</td>
                                {visibleColumns.map(column => (
                                    <td key={column.key} style={{ textAlign: 'center' }}>
                                        {column.total}
                                    </td>
                                ))}
                                <td style={{ textAlign: 'center' }}>
                                    {totalServices}
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>

                    {visibleColumns.length > 0 && (
                        <div className="services-summary-pie">
                            <h3 style={{ textAlign: 'center', marginBottom: '20px', fontSize: '1.1em' }}>
                                {pieChartTitle}
                            </h3>
                            {/* Fills the box below the heading, so the legend stays inside it. */}
                            <div className="services-summary-pie-chart">
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={pieChartData}
                                        cx="50%"
                                        cy="45%"
                                        labelLine={false}
                                        outerRadius={100}
                                        dataKey="value"
                                    >
                                        {pieChartData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        formatter={(value: number, name: string) => [`${value} tjänster`, name]}
                                    />
                                    <Legend/>
                                </PieChart>

                            </ResponsiveContainer>
                            </div>
                        </div>
                    )}
                </div>
                <div style={{ marginTop: '15px', fontSize: '0.9em', color: '#666', fontStyle: 'italic' }}>
                    Totalt {totalEntries} tjänster utförda under vald period av {activeServiceTypes} olika tjänstetyper
                    <br />
                    <span style={{ fontSize: '0.85em' }}>
                        Grå rader visar tjänster som inte utfördes under perioden. Kolumner visar fördelning per kategori.
                    </span>
                </div>
            </div>
        </div>
    );
};

export default ServicesSummarySection;